import React, { useEffect, useRef, useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from 'recharts';

interface ChartData {
  type: 'line' | 'bar' | 'pie' | 'area';
  title?: string;
  data: Array<Record<string, any>>;
  xKey?: string;
  yKey?: string;
  keys?: string[]; // For multiple data series
  nameKey?: string;
  valueKey?: string;
  colors?: string[];
  legend?: boolean;
  footnote?: string; // Optional footnote for chart interpretation
}

interface ChartRendererProps {
  chartJson: string;
}

// Blue theme colors matching your site's palette
const COLORS = [
  '#1d4ed8', // blue-700
  '#1e40af', // blue-800
  '#2563eb', // blue-600
  '#1e3a8a', // blue-900
  '#3730a3', // indigo-800
  '#312e81', // indigo-900
  '#60a5fa', // blue-400
  '#3b82f6', // blue-500
];

export const ChartRenderer: React.FC<ChartRendererProps> = ({ chartJson }) => {
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    // Don't try to parse if JSON is too short
    if (!chartJson || chartJson.trim().length < 20) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      setError(null);
    }

    // Debounce parsing - wait for streaming to finish
    timeoutRef.current = setTimeout(() => {
      try {
        const trimmed = chartJson.trim();

        // Validate JSON is complete (has closing brace)
        if (!trimmed.endsWith('}') && !trimmed.endsWith(']')) {
          // Still streaming, don't show error
          return;
        }

        const parsed = JSON.parse(trimmed);

        // Debug logging
        console.log('Parsed chart data:', parsed);

        // Validate chart structure
        if (!parsed.type || !parsed.data || !Array.isArray(parsed.data)) {
          setError('Invalid chart format: missing type or data array');
          return;
        }

        if (parsed.data.length === 0) {
          setError('Chart data is empty');
          return;
        }

        // Set default keys if not provided
        if (!parsed.xKey && parsed.data[0]) {
          const keys = Object.keys(parsed.data[0]);
          parsed.xKey = keys[0];
        }

        // If keys array is provided, use it for multi-series charts
        if (!parsed.keys && !parsed.yKey && parsed.data[0]) {
          const allKeys = Object.keys(parsed.data[0]);
          // Filter out the xKey to get data keys
          parsed.keys = allKeys.filter(key => key !== parsed.xKey);
        } else if (!parsed.keys && parsed.yKey) {
          // Single series - wrap yKey in array
          parsed.keys = [parsed.yKey];
        }

        if (!parsed.nameKey) {
          parsed.nameKey = parsed.xKey;
        }

        if (!parsed.valueKey) {
          parsed.valueKey = parsed.yKey || (parsed.keys && parsed.keys[0]);
        }

        console.log('Final chart data with keys:', parsed);
        console.log('Data keys to plot:', parsed.keys);

        setChartData(parsed);
        setError(null);
      } catch (err) {
        // Only show error if JSON looks complete
        const trimmed = chartJson.trim();
        if (trimmed.length > 50 && (trimmed.endsWith('}') || trimmed.endsWith(']'))) {
          console.error('Chart parsing error:', err);
          setError('Invalid chart JSON format');
        }
      }
    }, 500); // Wait 500ms after last update

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [chartJson]);

  if (error) {
    return (
      <div className="my-4 p-4 bg-red-50 border border-red-200 rounded">
        <p className="text-sm text-red-600">{error}</p>
        <details className="mt-2">
          <summary className="text-xs text-red-500 cursor-pointer">Show raw data</summary>
          <pre className="text-xs mt-2 overflow-auto">{chartJson}</pre>
        </details>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="my-4 p-4 bg-blue-50 border border-blue-200 rounded">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <p className="text-sm text-blue-600">Loading chart...</p>
        </div>
      </div>
    );
  }

  const renderChart = () => {
    const commonProps = {
      data: chartData.data,
      margin: { top: 10, right: 30, left: 0, bottom: 60 },
    };

    // Use custom colors if provided, otherwise use default COLORS
    const chartColors = chartData.colors || COLORS;

    // Determine which keys to plot
    const dataKeys = chartData.keys || (chartData.yKey ? [chartData.yKey] : []);

    switch (chartData.type) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={chartData.xKey}
              stroke="#1e3a8a"
              tick={{ fill: '#1e3a8a', fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              stroke="#1e3a8a"
              tick={{ fill: '#1e3a8a', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
              }}
            />
            {chartData.legend !== false && <Legend />}
            {dataKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={chartColors[index % chartColors.length]}
                strokeWidth={2}
                dot={{ fill: chartColors[index % chartColors.length], r: 4 }}
                activeDot={{ r: 6 }}
              >
                <LabelList dataKey={key} position="top" style={{ fill: '#1e3a8a', fontSize: '12px', fontWeight: 'bold' }} />
              </Line>
            ))}
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={chartData.xKey}
              stroke="#1e3a8a"
              tick={{ fill: '#1e3a8a', fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              stroke="#1e3a8a"
              tick={{ fill: '#1e3a8a', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
              }}
            />
            {chartData.legend !== false && <Legend />}
            {dataKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                fill={chartColors[index % chartColors.length]}
                radius={[4, 4, 0, 0]}
              >
                <LabelList dataKey={key} position="top" style={{ fill: '#1e3a8a', fontSize: '12px', fontWeight: 'bold' }} />
              </Bar>
            ))}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis
              dataKey={chartData.xKey}
              stroke="#1e3a8a"
              tick={{ fill: '#1e3a8a', fontSize: 11 }}
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis
              stroke="#1e3a8a"
              tick={{ fill: '#1e3a8a', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
              }}
            />
            {chartData.legend !== false && <Legend />}
            {dataKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={chartColors[index % chartColors.length]}
                fill={chartColors[index % chartColors.length]}
                fillOpacity={0.3}
              >
                <LabelList dataKey={key} position="top" style={{ fill: '#1e3a8a', fontSize: '12px', fontWeight: 'bold' }} />
              </Area>
            ))}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={chartData.data}
              dataKey={chartData.valueKey || 'value'}
              nameKey={chartData.nameKey || 'name'}
              cx="50%"
              cy="50%"
              outerRadius={100}
              label
            >
              {chartData.data.map((_entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
              }}
            />
            <Legend />
          </PieChart>
        );

      default:
        return <div className="text-sm text-red-600">Unsupported chart type: {chartData.type}</div>;
    }
  };

  return (
    <div className="my-4 p-4 bg-white border border-gray-200 rounded-lg overflow-hidden">
      {chartData.title && (
        <h3 className="text-lg font-semibold text-blue-900 mb-4">{chartData.title}</h3>
      )}
      <ResponsiveContainer width="100%" height={350}>
        {renderChart()}
      </ResponsiveContainer>
      {chartData.footnote && (
        <p className="mt-3 text-xs text-gray-600 italic border-t border-gray-200 pt-2 break-words whitespace-normal">
          <span className="font-semibold not-italic">Note: </span>
          {chartData.footnote}
        </p>
      )}
    </div>
  );
};
